export type ProxyMode = 'approver' | 'requestor' | 'float'
export type ManualTimeOffMode = 'manual-timeoff'

export const MODE_CONFIG: Record<
  ProxyMode,
  { title: string; bgColor: string }
> = {
  approver: {
    title: 'Assign an Approver Proxy to this Approver Group',
    bgColor: '#EBF6EB',
  },
  requestor: {
    title: 'Assign a Requestor Proxy to the selected Approver/Group',
    bgColor: '#FEF0E7',
  },
  float: {
    title: 'Assign a Float Employee to this Approver Group',
    bgColor: '#F2F8FB',
  },
}

// Shared search configuration for employee search (used in ProxySearch, ProxyEditPanel, ManualTimeOff)
export const EMPLOYEE_SEARCH_CONFIG = {
  searchOptions: [
    { key: 'name', label: 'Employee First or Last Name' },
    { key: 'nuid', label: 'Employee NUID' },
  ],
  fieldMeta: {
    firstName: {
      id: 1,
      label: 'First Name',
      name: 'firstName',
      placeholder: 'First name',
      rowGroup: 'names',
      containerClass: 'w-full desktop:w-[350px]',
    },
    lastName: {
      id: 2,
      label: 'Last Name',
      name: 'lastName',
      placeholder: 'Last name',
      rowGroup: 'names',
      containerClass: 'w-full desktop:w-[350px]',
    },
    nuid: {
      id: 3,
      label: 'Employee NUID',
      name: 'nuid',
      placeholder: 'Enter NUID',
    },
  },
  fieldsByMode: {
    name: ['firstName', 'lastName'],
    nuid: ['nuid'],
  },
}

// Shared search configuration for proxy search (used in ProxySearch, ProxyEditPanel)
export const PROXY_SEARCH_CONFIG = {
  searchOptions: [
    { key: 'nuid', label: 'Primary Approver NUID' },
    {
      key: 'name',
      label: 'Primary Approver FirstName or Primary Approver LastName',
    },
    { key: 'group', label: 'Primary Approval Group ID' },
  ],
  fieldMeta: {
    nuid: {
      id: 1,
      label: 'Primary Approver NUID',
      name: 'nuid',
      placeholder: 'Enter Primary Approver NUID',
    },
    firstName: {
      id: 2,
      label: 'Primary Approver FirstName',
      name: 'firstName',
      placeholder: 'Enter Primary Approver FirstName',
      rowGroup: 'names',
      containerClass: 'w-full desktop:w-[350px]',
    },
    lastName: {
      id: 3,
      label: 'Primary Approver LastName',
      name: 'lastName',
      placeholder: 'Enter Primary Approver LastName',
      rowGroup: 'names',
      containerClass: 'w-full desktop:w-[350px]',
    },
    groupId: {
      id: 4,
      label: 'Primary Approval Group ID',
      name: 'groupId',
      placeholder: 'Enter Primary Approval Group ID',
    },
  },
  fieldsByMode: {
    nuid: ['nuid'],
    name: ['firstName', 'lastName'],
    group: ['groupId'],
  },
}

/**
 * Search form configuration for the Manual Time-Off page.
 * Name search uses separate First Name and Last Name fields; the values are
 * sent as `searchFName` and `searchLName` to the API.
 */
export const MANUAL_TIME_OFF_SEARCH_CONFIG = {
  searchOptions: [
    { key: 'name', label: 'Employee First or Last Name' },
    { key: 'nuid', label: 'Employee NUID' },
  ],
  fieldMeta: {
    firstName: {
      id: 1,
      label: 'First Name',
      name: 'firstName',
      placeholder: 'First name',
      rowGroup: 'names',
      containerClass: 'w-full desktop:w-[350px]',
    },
    lastName: {
      id: 2,
      label: 'Last Name',
      name: 'lastName',
      placeholder: 'Last name',
      rowGroup: 'names',
      containerClass: 'w-full desktop:w-[350px]',
    },
    nuid: {
      id: 3,
      label: 'Employee NUID',
      name: 'nuid',
      placeholder: 'Enter NUID',
    },
  },
  fieldsByMode: {
    name: ['firstName', 'lastName'],
    nuid: ['nuid'],
  },
}
export interface ProxySearchResult {
  id: string
  name: string
  firstName?: string
  lastName?: string
  email: string
  department?: string
  primaryNuid?: string
  nuid?: string
  requestorNuid?: string
  approverProxy?: string
  floatEmployee?: string
  groupId?: string
  /** Approval group identifier returned by the requestor-proxy BFF search API. */
  approvalGrpId?: string
  /** Full approver name + NUID returned by the requestor-proxy BFF search API. */
  approver?: string
  /** Semicolon-delimited list of requestor proxy names/NUIDs from the BFF search API. */
  reqstorProxy?: string
  /** Geographic region display name returned by search APIs (searchAP, searchFE). */
  region?: string
  /** Region code for assignment operations, e.g. "NCA", "SCA" (from searchAP, searchFE). */
  regionCode?: string
}

export interface ProxyGroup {
  id: string
  groupName: string
}

export interface ProxySearchValues {
  // allow the modes used by the approver-style UI
  searchBy: 'name' | 'email' | 'id' | 'nuid' | 'group'
  searchValue: string
}
